import type { TemplateComponent, TemplateRelease } from '../types/template';

const loadedScripts = new Map<string, Promise<void>>();
const loadedStylesheets = new Set<string>();

declare global {
  interface Window {
    __OWNER_TEMPLATES__?: Record<
      string,
      Record<string, TemplateComponent>
    >;
  }
}

function loadScript(url: string): Promise<void> {
  const existing = loadedScripts.get(url);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load template: ${url}`));

    document.head.appendChild(script);
  });

  loadedScripts.set(url, promise);
  return promise;
}

function loadStylesheet(url: string): Promise<void> {
  if (loadedStylesheets.has(url)) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    link.onload = () => {
      loadedStylesheets.add(url);
      resolve();
    };

    link.onerror = () =>
      reject(new Error(`Failed to load template CSS: ${url}`));

    document.head.appendChild(link);
  });
}

export async function loadTemplate(
  release: TemplateRelease
): Promise<TemplateComponent> {
  if (release.stylesheet) {
    await loadStylesheet(release.stylesheet);
  }

  await loadScript(release.entrypoint);

  const component = window.__OWNER_TEMPLATES__?.[String(release.key)]?.[
    String(release.version)
  ];

  if (!component) {
    throw new Error(
      `Template ${release.key} version ${release.version} was not registered.`
    );
  }

  return component;
}
